import axios from '@/axios.js'
import { normalizeAddress } from '@/helpers/address.js'
import { buildEfacturaXml } from '@/helpers/efacturaUbl.js'

/**
 * Charge, pour UNE facture, exactement l'état que la fiche a en mémoire quand
 * l'opérateur clique « Send eFactura », puis en construit le XML.
 *
 * ---------------------------------------------------------------------------
 * POURQUOI PAS LE MIXIN `documentTotals` NI LE STORE `operationList`
 * ---------------------------------------------------------------------------
 * `fetchDocumentLines` écrit `this.productlist` / `this.documentTotals` sur le
 * composant, et `operationList/fetchOperation` fait un `commit` dans le store
 * Vuex global. Dans une boucle sur N factures, chaque tour écraserait l'état du
 * précédent : c'est très exactement le mécanisme de contamination entre
 * documents que le mixin `documentTotals` a été écrit pour éliminer (la facture
 * partie chez le client avec le total d'une autre). Ici, chaque facture repart
 * d'un contexte neuf, local, jeté après usage.
 */

/**
 * @param {number|string} idoperation
 * @returns {Promise<Object>} contexte attendu par `buildEfacturaXml`
 */
export async function loadEfacturaContext (idoperation) {
  const { data: operation } = await axios.get(`/operation/data/${idoperation}`)

  const person = operation && operation.people && operation.people[0]
  if (!person) {
    throw new Error(`Facture ${idoperation} : aucun client rattaché, envoi impossible.`)
  }

  // La langue n'est PAS un détail d'affichage : `/getdocumentlines` la fait
  // porter sur le texte des descriptions (qui devient `cbc:Item/cbc:Name`) ET
  // sur l'ORDER BY, donc sur la numérotation des lignes et sur celle qui absorbe
  // le résidu d'arrondi. Un repli `|| 1` fabriquerait silencieusement un XML
  // différent de celui que produit la fiche : mieux vaut échouer.
  const idlanguage = person.language && person.language.id
  if (!idlanguage) {
    throw new Error(
      `Facture ${idoperation} : la langue du client « ${person.company_name || '?'} » est inconnue. ` +
      'Renseignez-la sur la fiche client avant d\'envoyer l\'e-factura.'
    )
  }

  const [billingRes, linesRes] = await Promise.all([
    // Le contrôleur ignore le premier paramètre ; la fiche passe elle aussi deux
    // fois l'idoperation (cf. getBillingAndDeliveryAddressListDetails).
    axios.get(`/person/getaddressbillingdetails/${idoperation}/${idoperation}`),
    axios.get(`/getdocumentlines/${idoperation}/${idlanguage}`)
  ])

  const payload = linesRes.data || {}
  const lines = Array.isArray(payload.lines) ? [...payload.lines] : []
  if (!lines.length) {
    throw new Error(`Facture ${idoperation} : aucune ligne de produit, rien à envoyer.`)
  }

  return {
    operation,
    // La fiche avale l'erreur d'adresse et laisse le serveur refuser ; ici on
    // veut le motif tout de suite, sur la bonne ligne du lot.
    billingAddress: normalizeAddress(billingRes.data && billingRes.data[0]),
    lines,
    totals: payload.totals || null,
    idoperation
  }
}

/** Contexte + XML en un appel. */
export async function buildEfacturaXmlFor (idoperation) {
  return buildEfacturaXml(await loadEfacturaContext(idoperation))
}
